import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Flex,
  Text,
  Badge,
  Spinner,
  HStack,
  IconButton,
  Tooltip,
  useToast,
  Switch,
  useDisclosure,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
} from '@chakra-ui/react';
import { SearchIcon, DeleteIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

// How the account was created, recorded at sign-up (older accounts have none).
const SIGNUP_CLIENTS = { codex: 'Codex', claude: 'Claude Code', cursor: 'Cursor', 'claude-desktop': 'Claude Desktop', lovable: 'Lovable', bolt: 'Bolt', v0: 'v0', replit: 'Replit' };
function signupSourceLabel(user) {
  const client = SIGNUP_CLIENTS[user.signup_client] || user.signup_client;
  if (user.signup_source === 'client_install') return `Via ${client || 'an AI tool'} install`;
  if (user.signup_source === 'brief') return client ? `Via Brief, for ${client}` : 'Via Brief';
  if (user.signup_source === 'app_link') return 'Via a Pabs link';
  if (user.signup_source === 'direct') return 'Direct sign-up';
  return null;
}

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sendingReset, setSendingReset] = useState({});
  const [togglingStatus, setTogglingStatus] = useState({});
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAdmins, setShowAdmins] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [revokingMcp, setRevokingMcp] = useState({});
  const [switchingPlan, setSwitchingPlan] = useState({});
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const usersPerPage = 20;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/users?page=${currentPage}&limit=${usersPerPage}&sort=${sortBy}`);
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePasswordReset = async (e, user) => {
    e.stopPropagation();
    
    setSendingReset(prev => ({ ...prev, [user.id]: true }));
    
    try {
      const response = await fetchWithAuth(`/api/admin/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email }),
      });

      if (response.ok) {
        toast({
          title: 'Reset email sent',
          description: `Password reset link sent to ${user.email}`,
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error('Failed to send reset');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not send password reset',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSendingReset(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleToggleStatus = async (user) => {
    setTogglingStatus(prev => ({ ...prev, [user.id]: true }));
    
    try {
      const newStatus = user.is_active !== 1;
      const response = await fetchWithAuth(`/api/admin/user/${user.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (response.ok) {
        setUsers(prev => prev.map(u => 
          u.id === user.id ? { ...u, is_active: newStatus ? 1 : 0 } : u
        ));
        toast({
          title: newStatus ? 'User activated' : 'User deactivated',
          description: `${user.email} has been ${newStatus ? 'activated' : 'deactivated'}`,
          status: 'success',
          duration: 3000,
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Could not update user status',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setTogglingStatus(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleSwitchPlan = async (user) => {
    const targetPlan = user.plan_type === 'pro' ? 'free' : 'pro';
    setSwitchingPlan(prev => ({ ...prev, [user.id]: true }));
    try {
      const endpoint = targetPlan === 'pro'
        ? `/api/subscription/admin/switch-to-pro/${user.id}`
        : `/api/subscription/admin/switch-to-free/${user.id}`;
      const response = await fetchWithAuth(endpoint, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        setUsers(prev => prev.map(u =>
          u.id === user.id ? { ...u, plan_type: targetPlan } : u
        ));
        toast({ title: data.message, status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Failed to switch plan', status: 'error', duration: 3000 });
      }
    } catch {
      toast({ title: 'Could not switch plan', status: 'error', duration: 3000 });
    } finally {
      setSwitchingPlan(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleRevokeMcp = async (e, user) => {
    e.stopPropagation();
    setRevokingMcp(prev => ({ ...prev, [user.id]: true }));
    try {
      const response = await fetchWithAuth(`/api/admin/user/${user.id}/revoke-mcp-tokens`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(prev => prev.map(u =>
          u.id === user.id ? { ...u, mcp_tokens_count: '0' } : u
        ));
        toast({
          title: 'MCP tokens revoked',
          description: `${data.revoked_count} token(s) revoked for ${user.email}`,
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error('Failed to revoke');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not revoke MCP tokens',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setRevokingMcp(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    onDeleteOpen();
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`/api/admin/user/${userToDelete.id}/cascade`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
        toast({
          title: 'User deleted',
          description: `${userToDelete.email} and all associated data have been permanently deleted`,
          status: 'success',
          duration: 5000,
        });
        console.log('Deleted counts:', data.deleted);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Could not delete user',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
      onDeleteClose();
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAdminFilter = showAdmins || !user.is_admin;
    return matchesSearch && matchesAdminFilter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>Users</Heading>

      <Flex mb={4} gap={4} align="center" wrap="wrap">
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="white"
          />
        </InputGroup>
        
        <HStack spacing={2} bg="gray.50" px={3} py={2} borderRadius="md">
          <Text fontSize="sm" color="gray.600">Show admins</Text>
          <Switch
            size="sm"
            isChecked={showAdmins}
            onChange={(e) => setShowAdmins(e.target.checked)}
            colorScheme="purple"
          />
        </HStack>

        <Select
          maxW="190px"
          size="sm"
          bg="white"
          value={sortBy}
          onChange={handleSortChange}
        >
          <option value="recent">Recent user activity</option>
          <option value="newest">Newest registered</option>
          <option value="plans">Most plans</option>
        </Select>
      </Flex>

      {loading ? (
        <Box textAlign="center" py={10}>
          <Spinner size="lg" />
        </Box>
      ) : (
        <>
          <Box bg="white" borderRadius="md" overflow="hidden" boxShadow="sm">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>User</Th>
                  <Th>Status</Th>
                  <Th>Registered</Th>
                  <Th>Last User Activity</Th>
                  <Th isNumeric>MCP</Th>
                  <Th>Plan</Th>
                  <Th>Role</Th>
                  <Th w="120px">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers.map((user) => (
                  <Tr key={user.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <Box>
                        <Text fontWeight="medium" fontSize="sm">
                          {user.first_name || user.last_name
                            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                            : 'No name'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">{user.email}</Text>
                      </Box>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip 
                          label={user.is_admin ? "Cannot deactivate admins" : (user.is_active === 1 ? "Click to deactivate" : "Click to activate")} 
                          placement="top"
                        >
                          <Box>
                            <Switch
                              size="sm"
                              isChecked={user.is_active === 1}
                              isDisabled={user.is_admin || togglingStatus[user.id]}
                              onChange={() => handleToggleStatus(user)}
                              colorScheme="green"
                            />
                          </Box>
                        </Tooltip>
                        <Badge 
                          colorScheme={user.is_active === 1 ? 'green' : 'red'}
                          variant="subtle"
                          fontSize="xs"
                        >
                          {user.is_active === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </HStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">{formatDate(user.created_at)}</Text>
                      {signupSourceLabel(user) && <Text fontSize="xs" color="gray.500">{signupSourceLabel(user)}</Text>}
                    </Td>
                    <Td>
                      <Tooltip label={formatDate(user.last_user_activity || user.last_active)} placement="top">
                        <Text fontSize="sm" color="gray.600" cursor="default">
                          {formatRelativeTime(user.last_user_activity || user.last_active)}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td isNumeric>
                      {parseInt(user.mcp_tokens_count) > 0 ? (
                        <Badge colorScheme="cyan" variant="solid" minW="24px" textAlign="center">
                          {user.mcp_tokens_count}
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="outline" minW="24px" textAlign="center">0</Badge>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Badge
                          colorScheme={user.plan_type === 'pro' ? 'brand' : 'gray'}
                          fontSize="xs"
                          textTransform="capitalize"
                        >
                          {user.plan_type || 'free'}
                        </Badge>
                        <Tooltip
                          label={user.plan_type === 'pro' ? 'Move to Free' : 'Move to Pro'}
                          placement="top"
                        >
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme={user.plan_type === 'pro' ? 'red' : 'brand'}
                            isLoading={switchingPlan[user.id]}
                            onClick={() => handleSwitchPlan(user)}
                          >
                            {user.plan_type === 'pro' ? '↓ Free' : '↑ Pro'}
                          </Button>
                        </Tooltip>
                      </HStack>
                    </Td>
                    <Td>
                      {user.is_admin ? (
                        <Badge colorScheme="purple" size="sm">Admin</Badge>
                      ) : (
                        <Badge colorScheme="gray" size="sm">User</Badge>
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        {parseInt(user.mcp_tokens_count) > 0 && (
                          <Tooltip label="Revoke all MCP tokens" placement="top">
                            <IconButton
                              aria-label="Revoke MCP tokens"
                              icon={
                                <Text className="material-icons-outlined" fontSize="18px">
                                  link_off
                                </Text>
                              }
                              size="sm"
                              variant="ghost"
                              colorScheme="orange"
                              isLoading={revokingMcp[user.id]}
                              onClick={(e) => handleRevokeMcp(e, user)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Send password reset email" placement="top">
                          <IconButton
                            aria-label="Reset password"
                            icon={
                              <Text className="material-icons-outlined" fontSize="18px">
                                lock_reset
                              </Text>
                            }
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            isLoading={sendingReset[user.id]}
                            onClick={(e) => handlePasswordReset(e, user)}
                          />
                        </Tooltip>
                        <Tooltip label={user.is_admin ? "Cannot delete admins" : "Delete user and all data"} placement="top">
                          <IconButton
                            aria-label="Delete user"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            isDisabled={user.is_admin}
                            onClick={() => handleDeleteClick(user)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex mt={4} justify="space-between" align="center">
            <Text fontSize="sm" color="gray.500">
              Showing {filteredUsers.length} users
            </Text>
            <HStack>
              <Button
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Text fontSize="sm">Page {currentPage}</Text>
              <Button
                size="sm"
                onClick={() => setCurrentPage((p) => p + 1)}
                isDisabled={users.length < usersPerPage}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </>
      )}

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete User Permanently
            </AlertDialogHeader>

            <AlertDialogBody>
              {userToDelete && (
                <VStack align="start" spacing={3}>
                  <Text>
                    Are you sure you want to permanently delete <strong>{userToDelete.email}</strong>?
                  </Text>
                  <Box bg="red.50" p={3} borderRadius="md" w="100%">
                    <Text fontWeight="medium" color="red.700" mb={2}>
                      This will permanently delete:
                    </Text>
                    <VStack align="start" spacing={1} pl={2}>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.plans_count || 0} saved plan(s)
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.strategies_count || 0} product strategy report(s)
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.competitor_count || 0} competitor analysis report(s)
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.docs_count || 0} README document(s)
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.conversations_count || 0} chat conversation(s)
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {userToDelete.shared_plans_count || 0} share link(s)
                      </Text>
                    </VStack>
                  </Box>
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    This action cannot be undone.
                  </Text>
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleConfirmDelete} 
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete User
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default AdminUsers;
